import { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { fetchAssociatedTestCases } from '../../../modules/associations';
import { fetchIterationsByTestCycleId } from '../../../modules/executions';
import { getAssociatedUsers } from '../../../modules/project';
import TestExecutionSection from './TestExecutionSection';
import TestPlayerSidebar from './TestPlayerSidebar';

const PAGE_SIZE = 20;

const TestPlayerPageComponent = ({ projectId, testCycleId, testCycleData }) => {
  const [testCases, setTestCases] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [viewMode, setViewMode] = useState('iterations');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [testCasePage, setTestCasePage] = useState(1);
  const [totalTestCases, setTotalTestCases] = useState(0);

  const testExecutionRef = useRef(null);
   const preserveActiveTabRef = useRef(false);

  const setInitialActiveTab = (items) => {
    if (items.length > 0) setActiveTab(items[0]?.id);
  };

  const fetchIterations = async () => {
    try {
      const response = await fetchIterationsByTestCycleId(testCycleId);
      const data = response?.data || [];
      setIterations(data);
       if (viewMode === 'iterations') {
        if (!preserveActiveTabRef.current) {
          setInitialActiveTab(data);
        }
        preserveActiveTabRef.current = false;
      }
    } catch {
      setError('Failed to fetch iterations.');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getAssociatedUsers(projectId);
      setUsers(
        response?.map((item) => ({
          accountId: item?.user?.id,
          displayName: item?.user?.full_name,
        })) || []
      );
    } catch {
      setError('Failed to fetch users.');
    }
  };

  const fetchTestCases = async (page = 1, append = false) => {
    try {
      setLoading(true);
      const response = await fetchAssociatedTestCases(
        testCycleId,
        projectId,
        null,
        null,
        'Approved',
        null,
        page,
        PAGE_SIZE,
        true,
      );

      const fetched = response?.associations || [];
      setTestCases((prev) => (append ? [...prev, ...fetched] : fetched));
      setTotalTestCases(response?.total || 0);
      setTestCasePage(page);

      if (!append && viewMode === 'testcases' && fetched.length > 0) {
        setActiveTab(fetched[0].id);
      }
    } catch {
      setError('Failed to fetch test cases.');
    } finally {
      setLoading(false);
    }
  };
  const loadMoreTestCases = () => {
    const nextPage = testCasePage + 1;
    if ((nextPage - 1) * PAGE_SIZE < totalTestCases) {
      fetchTestCases(nextPage, true);
    }
  };

  const handleViewModeChange = async (mode) => {
    setViewMode(mode);
    if (mode === "iterations") {
      setInitialActiveTab(iterations);
      return;
    }

    if (mode === "testcases") {
      setActiveTab(null);
      if (testCases.length === 0) {
        await fetchTestCases(1, false);
      } else {
        setActiveTab(testCases[0].id);
      }
    }
  };

  const handleTabClick = (id) => {
    setActiveTab(id);
    if (testExecutionRef.current) {
      testExecutionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
   const handleIterationEdited = (editedIterationId) => {
    preserveActiveTabRef.current = true; 
    setActiveTab(editedIterationId); 
  };
  useEffect(() => {
    if (!testCycleId) return;

    setError('');
    const loadEverythingExceptTestCases = async () => {
      setLoading(true);
      await Promise.all([fetchIterations(), fetchUsers()]);
      setLoading(false);
    };
    loadEverythingExceptTestCases();
  }, [testCycleId]);

  const hasMoreTestCases = testCasePage * PAGE_SIZE < totalTestCases;

  return (
    <Container fluid>
      {loading && testCases.length === 0 && viewMode === "testcases" ? (
        <span className="d-flex justify-content-center align-items-center text-muted mt-3">
          Loading...</span>
      ) : error ? (
        <div className="text-danger">{error}</div>
      ) : (
        <div className="d-flex flex-row w-100" style={{ minHeight: '100vh' }}>
          {iterations.length > 0 && (
            <TestPlayerSidebar
              testCases={testCases}
              activeTab={activeTab}
              loading={loading}
              handleTabClick={handleTabClick}
              users={users}
              viewMode={viewMode}
              handleViewModeChange={handleViewModeChange}
              iterations={iterations}
              loadMoreTestCases={loadMoreTestCases}
              hasMoreTestCases={hasMoreTestCases}
            />
          )}
          <div className="flex-grow-1 px-2 mt-2" ref={testExecutionRef}>
            <TestExecutionSection
              activeTab={activeTab}
              setActiveTab={handleIterationEdited}
              currentIteration={iterations?.find(it => it.id === activeTab)}
              testCycleData={testCycleData}
              viewMode={viewMode}
              noOfIterations={iterations.length}
              getIterationsByTestCycleId={fetchIterations}
            />
          </div>
        </div>
      )}
    </Container>
  );
};

export default TestPlayerPageComponent;
